import User from 'src/internal/store/utils/user'

const GETTERS = {
  self(state) {
    return state.self || state.user || {}
  },

  selfEmail(state, getters) {
    const { self } = getters
    const { email, personEmail } = self

    return email || personEmail
  },
  userEmail(state, getters) {
    const { aliasEmail, inAliasMode, selfEmail } = getters
    return inAliasMode ? aliasEmail : selfEmail
  },
  personId(state) {
    const { user } = state
    return user.personId
  },
  managerPersonId(state) {
    const { user } = state
    return user.managerPersonId
  },

  userName(state) {
    const { user } = state
    return (
      user && user.personName
    ) || null
  },

  /* Roles */

  isActiveUser(state) {
    return User.isActiveUser(state.user)
  },
  isInternalUser(state) {
    return state.user && state.user.isPersonUserInternal
  },
  isInternalWriter(state, getters) {
    const { user } = state
    const isInternalWriter = () => {
      const { isInternalUser } = getters
      const { access: { level }, isAdmin } = user
      const isAdminUser = isAdmin && ['admin', 'writer'].includes(level)

      return isInternalUser && isAdminUser
    }

    return !!user && isInternalWriter()
  },
  isInternalAdminUser(state, getters) {
    const { user } = state
    const isInternalAdminUser = () => {
      const { isInternalUser } = getters
      const { access: { level }, isAdmin } = user
      const isAdminUser = isAdmin && level === 'admin'

      return isInternalUser && isAdminUser
    }

    return !!user && isInternalAdminUser()
  },
  billingCodes(state) {
    return state.user.billingCodes || []
  },

  isSelfActive(state, getters) {
    const { self } = getters
    const { access, isActive } = self
    const { active, role } = access || {}

    const isActiveInternal = () => role === 'internal' && active && isActive
    const isActiveExternal = () => role !== 'internal' && active

    return !!self && (
      isActiveInternal() || isActiveExternal()
    )
  },
  isSelfInternal(state, getters) {
    const { isSelfActive, self } = getters
    const { access, isInternal } = self
    const { role } = access || {}
    const isInternalUser = role === 'internal' && isInternal

    return isSelfActive && isInternalUser
  },
  isSelfInternalWriter(state, getters) {
    const { isSelfInternal, self } = getters
    const { access, isAdmin } = self
    const { level } = access || {}
    const isAdminSelf = isAdmin && ['admin', 'writer'].includes(level)

    return isSelfInternal && isAdminSelf
  },
  isSelfInternalAdmin(state, getters) {
    const { isSelfInternal, self } = getters
    const { access, isAdmin } = self
    const { level } = access || {}
    const isAdminSelf = isAdmin && level === 'admin'

    return isSelfInternal && isAdminSelf
  },

  /* Networks */

  networkPermissions(state, getters) {
    const { user = {} } = state
    const { inDemoMode } = getters
    const { network } = user

    const getDemoNetworks = () => User.makeDemoNetworks()
    const getUserNetworks = () => Array.isArray(network) && network

    return inDemoMode ? getDemoNetworks() : getUserNetworks() || getDemoNetworks()
  },
  networkSettings(state, getters) {
    return getters.settings.networks || false
  },

  networks(state, getters) {
    const { networkPermissions, networkSettings } = getters
    return User.getNetworks(getters.isInternalUser, networkPermissions, networkSettings)
  },
  defaultNetwork(state, getters) {
    const { networks } = getters

    const findPrimarySetting = () => networks.find((n) => n.isPrimary)
    const getFirstSetting = () => networks[0]

    return findPrimarySetting() || getFirstSetting()
  },
  defaultAssociationId(state, getters) {
    const { defaultNetwork } = getters
    return defaultNetwork && defaultNetwork.associationId
  },
  defaultSubId(state, getters) {
    const { defaultNetwork } = getters
    return defaultNetwork && defaultNetwork.subId
  },

  /* Permissions */

  selfPermissions(state, getters) {
    const { self = {} } = getters
    const { access = {} } = self

    return access.permissions || {}
  },
  permissions(state) {
    const { user = {} } = state
    const { access = {} } = user || {}

    return access.permissions || {}
  },
  isAdManagerUser(state, getters) {
    const { permissions = {} } = getters

    return permissions.admanager || false
  },
  isDevUser(state, getters) {
    const { isInternalAdminUser, permissions = {} } = getters

    return (
      isInternalAdminUser && permissions.dev
    ) || false
  },
  isProgrammaticUser(state, getters) {
    const { isInternalUser, permissions = {} } = getters

    return isInternalUser || permissions.programmatic || false
  },
  isCustomAnalyticUser(state, getters) {
    const { permissions = {} } = getters
    const { customAnalytics, customReports } = permissions

    return !!customAnalytics || !!customReports || false
  },
  customAnalytics(state, getters) {
    const { isCustomAnalyticUser, permissions } = getters

    if (!isCustomAnalyticUser) return []

    return permissions.customAnalytics || permissions.customReports || []
  },
  customAnalyticsActive(state) {
    return state.user.customAnalyticsActive || []
  },

  /* Settings */

  selfSettings(state, getters) {
    const { self } = getters

    return (
      !!self && self.settings
    ) || {}
  },
  aliasSettings(state, getters) {
    const { selfSettings } = getters
    const { alias } = selfSettings

    return (
      alias && typeof alias === 'object' && alias.settings
    ) || {}
  },
  settings(state) {
    const { user } = state

    return (
      !!user && user.settings
    ) || {}
  },

  selfMessages(state, getters) {
    const { selfSettings } = getters

    return selfSettings.messages || false
  },
  messages(state, getters) {
    const { settings } = getters

    return settings.messages || false
  },

  bookmarks(state, getters) {
    return getters.settings.bookmarks || false
  },
  dashboardSettings(state, getters) {
    return getters.settings.dashboard || false
  },
  homeSettings(state, getters) {
    return getters.settings.home || false
  },

  search(state, getters, rootState) {
    const { settings } = getters
    const getDefaultSearchConfigs = () => (
      {
        maxResults: rootState.tools.MAX_SEARCH_RESULTS,
      }
    )
    return settings.search || getDefaultSearchConfigs()
  },
  maxSearchResults(state, getters, rootState) {
    const { search } = getters
    const { MAX_SEARCH_RESULTS } = rootState.tools
    return search.maxResults || MAX_SEARCH_RESULTS
  },

  /* Alias */

  alias(state, getters) {
    const { selfSettings } = getters
    return (
      selfSettings && typeof selfSettings === 'object' && typeof selfSettings.alias === 'object' && selfSettings.alias
    ) || {}
  },
  isAliasActive(state, getters) {
    const { alias } = getters
    return (
      alias && typeof alias === 'object' && alias.active
    ) || false
  },
  hasAliasPermission(state, getters) {
    const { selfPermissions } = getters
    return (
      selfPermissions && selfPermissions.alias
    ) || false
  },
  inAliasMode(state, getters) {
    return (
      getters.isSelfInternal || getters.hasAliasPermission
    ) && getters.isAliasActive
  },
  aliasEmail(state, getters) {
    const { alias } = getters
    return alias && typeof alias === 'object' && alias.email
  },
  isAliasWriter(state, getters) {
    return getters.isSelfInternal
  },

  /* Demo */

  hasDemoModePermissions(state, getters) {
    const { selfPermissions } = getters
    const { demo } = selfPermissions || {}

    return demo || false
  },
  isDemoExternal(state, getters) {
    const { hasDemoModePermissions, isSelfInternal } = getters

    return !isSelfInternal && hasDemoModePermissions
  },
  isDemoWriter(state, getters) {
    return getters.isSelfInternal
  },
  hasDemoModeOn(state, getters) {
    const { selfSettings } = getters
    const { demo } = selfSettings || {}
    const hasLegacyDemoStateOn = () => demo === true
    const hasModernDemoStateOn = () => typeof demo === 'object' && demo.active === true

    return demo && (
      hasLegacyDemoStateOn() || hasModernDemoStateOn()
    )
  },
  inDemoMode(state, getters) {
    const { demoMode } = state
    const { hasDemoModeOn, isDemoExternal, isSelfInternal } = getters
    const turnedDemoModeOn = demoMode === true || (
      typeof demoMode === 'object' && demoMode.active === true
    )

    return turnedDemoModeOn || (
      isDemoExternal && hasDemoModeOn
    ) || (
      isSelfInternal && hasDemoModeOn
    )
  },

  /* Dev Mode */

  hasDevPermission(state, getters) {
    const { isSelfInternalAdmin, selfPermissions } = getters
    return (
      isSelfInternalAdmin && selfPermissions && selfPermissions.dev
    ) || false
  },
  devSettings(state, getters) {
    const { hasDevPermission, selfSettings } = getters
    const getDevSettings = () => selfSettings
      && typeof selfSettings
      === 'object'
      && typeof selfSettings.dev
      === 'object'
      && selfSettings.dev
    return (
      hasDevPermission && getDevSettings()
    ) || {}
  },
  inDevMode(state, getters) {
    const { devSettings, hasDevPermission } = getters
    const isDevModeExplicitlyFalse = devSettings && typeof devSettings === 'object' && devSettings.active === false
    return hasDevPermission && !isDevModeExplicitlyFalse
  },

  /* Themes */

  themes(state, getters) {
    const { settings } = getters
    return settings.themes || {}
  },
  inDarkMode(state, getters, rootState) {
    const { darkMode } = rootState.layout
    const { themes } = getters

    return darkMode || themes.dark
  },
}

export default GETTERS
